import React, { useEffect, useImperativeHandle, forwardRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import Mention from '@tiptap/extension-mention';
import CharacterCount from '@tiptap/extension-character-count';
import Placeholder from '@tiptap/extension-placeholder';
import { ReactRenderer } from '@tiptap/react';
import tippy from 'tippy.js';
import { getMergeTagDictionaryEntries, getMergeTagGroupLabel } from '@bay-email/core';
import 'tippy.js/dist/tippy.css';
import styles from './TiptapInput.module.scss';

const allMergeTags = getMergeTagDictionaryEntries({ excludeImageFields: true })
	.filter(entry => entry.key.includes('.'))
	.map(entry => ({
		value: entry.key,
		label: entry.label,
		group: entry.group,
	}));

// Subject / preview text inputs only offer contact + date fields for insertion
// (mirrors the 'subject-preview' dropdown context — listing/user/legal groups
// are excluded). `allMergeTags` above is still used to render labels for tags
// already present in the content.
const suggestionMergeTags = allMergeTags.filter(
	tag => tag.group === 'contact' || tag.group === 'current_date',
);

// Suggestion list component
const MentionList = forwardRef((props: any, ref) => {
	const [selectedIndex, setSelectedIndex] = React.useState(0);
	const itemRefs = React.useRef<(HTMLButtonElement | null)[]>([]);

	const selectItem = (index: number) => {
		const item = props.items[index];
		if (item) {
			props.command({ id: item.value, label: item.label });
		}
	};

	useEffect(() => setSelectedIndex(0), [props.items]);

	useEffect(() => {
		itemRefs.current[selectedIndex]?.scrollIntoView({ block: 'nearest' });
	}, [selectedIndex]);

	useImperativeHandle(ref, () => ({
		onKeyDown: ({ event }: { event: KeyboardEvent }) => {
			if (event.key === 'ArrowUp') {
				setSelectedIndex(i => (i + props.items.length - 1) % props.items.length);
				return true;
			}

			if (event.key === 'ArrowDown') {
				setSelectedIndex(i => (i + 1) % props.items.length);
				return true;
			}

			if (event.key === 'Enter') {
				selectItem(selectedIndex);
				return true;
			}

			return false;
		},
	}));

	if (!props.items.length) {
		return (
			<div className={styles.mentionList}>
				<div className={styles.mentionItem}>No results</div>
			</div>
		);
	}

	// Group items by the prefix before the first dot (e.g. "contact", "user")
	const groups: { group: string; items: { item: any; flatIndex: number }[] }[] = [];
	props.items.forEach((item: any, flatIndex: number) => {
		const group = item.value.split('.')[0] ?? 'other';
		const last = groups[groups.length - 1];
		if (last && last.group === group) {
			last.items.push({ item, flatIndex });
		} else {
			groups.push({ group, items: [{ item, flatIndex }] });
		}
	});

	return (
		<div className={styles.mentionList}>
			{groups.map(({ group, items }, groupIndex) => (
				<div key={group}>
					{groupIndex > 0 && <hr className='my-1 border-secondary-subtle' />}
					<div
						className={`${styles.mentionGroupHeading} px-3 pt-2 pb-1 fw-bold text-uppercase text-secondary user-select-none fs-7`}
					>
						{getMergeTagGroupLabel(group) ?? group}
					</div>
					{items.map(({ item, flatIndex }) => (
						<button
							key={item.value}
							ref={el => {
								itemRefs.current[flatIndex] = el;
							}}
							className={`${styles.mentionItem} ${flatIndex === selectedIndex ? styles.selected : ''}`}
							type='button'
							onMouseEnter={() => setSelectedIndex(flatIndex)}
							onClick={() => selectItem(flatIndex)}
						>
							{item.label}
						</button>
					))}
				</div>
			))}
		</div>
	);
});

MentionList.displayName = 'MentionList';

interface TiptapInputProps {
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	characterLimit: number;
	onCharacterCount?: (count: number) => void;
}

export interface TiptapInputRef {
	insertEmoji: (emoji: string) => void;
	insertMention: (mergeTag: { value: string; label: string }) => void;
}

// Parse {{mergeTag}} format to Tiptap JSON
function parseToTiptapJson(text: string) {
	if (!text) {
		return {
			type: 'doc',
			content: [{ type: 'paragraph', content: [] }],
		};
	}

	const content: any[] = [];
	const regex = /\{\{([^}]+)\}\}/g;
	let lastIndex = 0;
	let match;

	while ((match = regex.exec(text)) !== null) {
		// Add text before merge tag
		if (match.index > lastIndex) {
			const textBefore = text.substring(lastIndex, match.index);
			if (textBefore) {
				content.push({ type: 'text', text: textBefore });
			}
		}

		// Add mention node
		const mergeTagValue = match[1];
		const mergeTag = allMergeTags.find(tag => tag.value === mergeTagValue);
		content.push({
			type: 'mention',
			attrs: {
				id: mergeTagValue,
				label: mergeTag?.label || mergeTagValue,
			},
		});

		lastIndex = match.index + match[0].length;
	}

	// Add remaining text
	if (lastIndex < text.length) {
		const textAfter = text.substring(lastIndex);
		if (textAfter) {
			content.push({ type: 'text', text: textAfter });
		}
	}

	return {
		type: 'doc',
		content: [
			{
				type: 'paragraph',
				content: content.length > 0 ? content : [],
			},
		],
	};
}

// Convert Tiptap JSON to {{mergeTag}} format
function convertToMergeTagFormat(json: any): string {
	if (!json || !json.content) return '';

	let result = '';
	json.content.forEach((node: any) => {
		if (node.type === 'paragraph' && node.content) {
			node.content.forEach((child: any) => {
				if (child.type === 'text') {
					result += child.text;
				} else if (child.type === 'mention') {
					result += `{{${child.attrs.id}}}`;
				}
			});
		}
	});
	return result;
}

export const TiptapInput = forwardRef<TiptapInputRef, TiptapInputProps>(
	({ value, onChange, placeholder, characterLimit, onCharacterCount }, ref) => {
		const editor = useEditor({
			extensions: [
				Document,
				Paragraph,
				Text,
				Placeholder.configure({
					placeholder: placeholder || 'Type here...',
				}),
				CharacterCount.configure({
					limit: characterLimit,
				}),
				Mention.extend({
					addAttributes() {
						return {
							...this.parent?.(),
							label: {
								default: null,
								parseHTML: element => element.getAttribute('data-label'),
								renderHTML: attributes => {
									if (!attributes.label) {
										return {};
									}
									return {
										'data-label': attributes.label,
									};
								},
							},
						};
					},
				}).configure({
					HTMLAttributes: {
						class: styles.mention,
					},
					renderLabel({ options, node }) {
						return node.attrs.label || node.attrs.id;
					},
					suggestion: {
						items: ({ query }: { query: string }) => {
							return suggestionMergeTags
								.filter(
									item =>
										item.label.toLowerCase().includes(query.toLowerCase()) ||
										item.value.toLowerCase().includes(query.toLowerCase()),
								)
								.slice(0, 20);
						},
						render: () => {
							let component: ReactRenderer;
							let popup: any;

							return {
								onStart: (props: any) => {
									component = new ReactRenderer(MentionList, {
										props,
										editor: props.editor,
									});

									if (!props.clientRect) {
										return;
									}

									popup = tippy('body', {
										getReferenceClientRect: props.clientRect,
										appendTo: () => document.body,
										content: component.element,
										showOnCreate: true,
										interactive: true,
										trigger: 'manual',
										placement: 'bottom-start',
									});
								},

								onUpdate(props: any) {
									component.updateProps(props);

									if (!props.clientRect) {
										return;
									}

									popup[0].setProps({
										getReferenceClientRect: props.clientRect,
									});
								},

								onKeyDown(props: any) {
									if (props.event.key === 'Escape') {
										popup[0].hide();
										return true;
									}

									return component.ref?.onKeyDown(props);
								},

								onExit() {
									popup[0].destroy();
									component.destroy();
								},
							};
						},
					},
				}),
			],
			content: parseToTiptapJson(value),
			onUpdate: ({ editor }) => {
				const json = editor.getJSON();
				const mergeTagText = convertToMergeTagFormat(json);
				onChange(mergeTagText);

				if (onCharacterCount) {
					onCharacterCount(editor.storage.characterCount.characters());
				}
			},
			editorProps: {
				attributes: {
					class: styles.tiptapEditor,
				},
			},
		});

		// Update editor content when value prop changes externally
		useEffect(() => {
			if (editor && value !== convertToMergeTagFormat(editor.getJSON())) {
				const newContent = parseToTiptapJson(value);
				editor.commands.setContent(newContent);
			}
		}, [value, editor]);

		// Update character count when editor is created or value changes
		useEffect(() => {
			if (editor && onCharacterCount) {
				onCharacterCount(editor.storage.characterCount.characters());
			}
		}, [editor, value, onCharacterCount]);

		// Expose methods to parent
		useImperativeHandle(ref, () => ({
			insertEmoji: (emoji: string) => {
				if (editor) {
					editor.chain().focus().insertContent(emoji).run();
				}
			},
			insertMention: (mergeTag: { value: string; label: string }) => {
				if (editor) {
					editor
						.chain()
						.focus()
						.insertContent({
							type: 'mention',
							attrs: {
								id: mergeTag.value,
								label: mergeTag.label,
							},
						})
						.run();
				}
			},
		}));

		return <EditorContent editor={editor} />;
	},
);

TiptapInput.displayName = 'TiptapInput';
